import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import TextEditor from '../../Common/Inputs/TextEditor';
import {
  ADMINISTRATOR_USER,
  ASSOCIATION_USER,
  INSTITUTION_USER,
  REPORT_USER,
  SPECIALTY_USER,
  SUPER_USER,
} from '../../../actionTypes/Users/UserActionType';
import FormButtons from '../../Common/FormParts/FormButtons';
import CheckList from '../../Common/Inputs/CheckList';
import DateTimePickerRow from '../../Common/Inputs/DateTimePickerRow';
import { format, isValid, isAfter, isPast, endOfDay } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { onErrorMsg } from '../../../actions/FlashMessage/FlashMessages';
import Api from '../../../Api';
import Constants from '../../../Constants';
import { loadAdminBanner } from '../../../actions/Notifications/AdminBanners';
import { setNavigateToMsg } from '../../../actions/Notifications/ScreenReaderInit';
import { setPageTitle } from '../../../actions/Page/PageAction';
import { Inputs, Loading } from '../../Common';
import './AddBanner.scss';

const userRoles = [
  { id: SUPER_USER, label: 'Super Users' },
  { id: ASSOCIATION_USER, label: 'Association Users' },
  { id: INSTITUTION_USER, label: 'Institution Users' },
  { id: ADMINISTRATOR_USER, label: 'Organization Administrator' },
  { id: REPORT_USER, label: 'Report User' },
  { id: SPECIALTY_USER, label: 'Specialty User' },
];

const MESSAGE_MAX_ALLOWED_CHARACTER = 160;

const { saveFormat } = Constants;

const AddBanner = (props) => {
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [message, setMessage] = useState('');
  const [messageLength, setMessageLength] = useState(0);
  const [failedFields, setFailedFields] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedUserRoles, setSelectedUserRoles] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const hasSelectedAllUser = userRoles.length === selectedUserRoles.length;
  const startDateDisabled = isEdit && isPast(startDate);

  const getTitle = () => {
    const msgId = getCurrentMessageId();
    const mainHeaderText = `${msgId ? 'Edit' : 'Create'} Banner Message`;
    return mainHeaderText;
  };

  const getCurrentMessageId = () => {
    const { computedMatch } = props;
    const paramId = computedMatch ? computedMatch.params.id : null;
    return paramId || id;
  };

  const initEditForm = async () => {
    setIsBusy(true);
    try {
      const msgId = getCurrentMessageId();
      const editedBanner = await loadAdminBanner(msgId);
      const startDate = new Date(editedBanner.startDate);
      const endDate = new Date(editedBanner.endDate);
      setStartDate(startDate);
      setEndDate(endDate);
      setSelectedUserRoles(editedBanner.userRole);
      setMessage(editedBanner.message);
    } catch (err) {
      console.error(err);
      dispatch(onErrorMsg());
    }
    setIsBusy(false);
  };

  const _submit = async () => {
    const { adminBanners } = Constants.apiUrls;

    const bannerJson = {
      message: message,
      startDate: format(startDate, saveFormat),
      endDate: format(endDate, saveFormat),
      userRole: selectedUserRoles,
    };

    const msgId = getCurrentMessageId();

    const apiFn = isEdit ? Api.instance.putData : Api.instance.postData;
    const apiUrl = isEdit ? `${adminBanners}/${msgId}` : adminBanners;

    setIsSaving(true);
    try {
      await apiFn.call(Api.instance, apiUrl, { body: bannerJson });
      navigate('/messageUsers', { statusMessage: `Banner message successfully ${isEdit ? 'saved' : 'created'}!` });
    } catch (error) {
      console.error(error);
      setErrorMsg(error?.response?.data?.error || 'Something went wrong. Please try again.');
      setFailedFields(['startDateInvalid', 'endDateInvalid']);
    }
    setIsSaving(false);
  };

  const validationForm = () => {
    const requiredFields = ['startDate', 'endDate', 'userRoles', 'message'];
    const failedFields = [];
    requiredFields.forEach((field) => {
      // eslint-disable-next-line default-case
      switch (field) {
        case 'startDate':
          if (!startDateDisabled) {
            if (!isValid(startDate)) {
              failedFields.push([field] + 'Invalid');
            } else if (isPast(endOfDay(startDate))) {
              failedFields.push('startDatePast');
            }
          }
          break;
        case 'endDate':
          if (!isValid(endDate)) {
            failedFields.push(`${[field]}Invalid`);
          } else if (isAfter(startDate, endDate)) {
            failedFields.push('endDateLess');
          }
          break;
        case 'userRoles':
          if (!selectedUserRoles.length) {
            failedFields.push(field);
          }
          break;
        case 'message':
          if (!messageLength) {
            failedFields.push(field);
          } else if (messageLength > MESSAGE_MAX_ALLOWED_CHARACTER) {
            failedFields.push('maxAllowedCharacters');
          }
          break;
      }
    });
    setErrorMsg('');
    setFailedFields(failedFields);
    return !failedFields.length;
  };

  const onSubmit = () => {
    const validationPass = validationForm();
    validationPass ? _submit() : void 0;
  };

  useEffect(() => {
    const msgId = getCurrentMessageId();
    dispatch(setNavigateToMsg(getTitle()));
    dispatch(setPageTitle(getTitle()));
    setIsEdit(!!msgId);
    msgId ? initEditForm() : void 0;
    return () => {
      dispatch(setPageTitle());
    };
  }, []);

  return (
    <form className="form-wrapper lsn-banner-form pTop-45" disabled={isBusy}>
      <h1 className="md-title form-title-center" id="app_page_title">
        {getTitle()}
      </h1>

      <Loading show={isBusy} backdrop classNames={['loading-banner']} />

      <Inputs
        required
        keepLabelTopAligned
        id="bannerTextEditor"
        title="Banner Message"
        errorMessages={failedFields.includes('maxAllowedCharacters') || failedFields.includes('message')}
        renderInput={() => (
          <TextEditor
            ariaLabel={'Banner Message'}
            data-testid="bannerTextEditor"
            hasValue={isEdit}
            textLength={setMessageLength}
            onChange={setMessage}
            htmlText={message}
          />
        )}
        renderCustomErrors={() => (
          <React.Fragment>
            <div
              hidden={!failedFields.includes('maxAllowedCharacters')}
              className={`input-error label-text ScalaSansOT`}>
              {messageLength} out of {MESSAGE_MAX_ALLOWED_CHARACTER} characters
            </div>
            <span hidden={!failedFields.includes('message')} className="input-error">
              Banner message can't be empty.
            </span>
          </React.Fragment>
        )}
      />

      <Inputs
        required
        id="startDateBanner"
        title="Start Display"
        errorMessages={failedFields.includes('startDateInvalid') || failedFields.includes('startDatePast')}
        renderInput={() => (
          <DateTimePickerRow
            name="start_date"
            inputId="startDateBanner"
            ariaLabel="please enter Start Display date format"
            disabled={startDateDisabled}
            hasError={failedFields.includes('startDateInvalid')}
            value={startDate}
            onSelectDate={setStartDate}
          />
        )}
        renderCustomErrors={() => (
          <React.Fragment>
            {failedFields.includes('startDateInvalid') && !errorMsg ? (
              <span id="startDateError" role="alert" className="input-error">
                Please select Start Display date/time.
              </span>
            ) : null}
            {failedFields.includes('startDatePast') && !errorMsg ? (
              <span id="startDateError" role="alert" className="input-error">
                Start Display can not be in past.
              </span>
            ) : null}
          </React.Fragment>
        )}
      />

      <Inputs
        required
        id="endDateBanner"
        title="End Display"
        errorMessages={
          errorMsg ||
          failedFields.includes('endDate') ||
          failedFields.includes('endDateInvalid') ||
          failedFields.includes('endDateLess')
        }
        renderInput={() => (
          <DateTimePickerRow
            name="end_date"
            inputId="endDateBanner"
            ariaLabel="please enter End Display date format"
            hasError={failedFields.includes('endDateInvalid')}
            value={endDate}
            onSelectDate={setEndDate}
          />
        )}
        renderCustomErrors={() => (
          <React.Fragment>
            {failedFields.includes('endDate') ? (
              <span id="endDateError" role="alert" className="input-error">
                End date can not be past.
              </span>
            ) : null}
            {failedFields.includes('endDateInvalid') && !errorMsg ? (
              <span id="endDateError" role="alert" className="input-error">
                Please select End Display date/time.
              </span>
            ) : null}
            {errorMsg ? (
              <span id="errorMsg" role="alert" className="input-error inline-block mTop-10">
                {errorMsg}
              </span>
            ) : null}
            {failedFields.includes('endDateLess') && !errorMsg ? (
              <span id="endDateError" role="alert" className="input-error">
                End Display can not be earlier that Start Display.
              </span>
            ) : null}
          </React.Fragment>
        )}
      />

      <Inputs
        required
        id="userRolesSelector"
        title="Display To"
        errorMessages={failedFields.includes('userRoles')}
        keepLabelTopAligned
        renderInput={() => (
          <div className={`roles-selector ${hasSelectedAllUser ? 'selected-all' : 'selected-custom'}`}>
            <CheckList
              data-testid="userRolesSelector"
              classNames="cb-top"
              titleName={'label'}
              items={[{ id: 0, label: 'All Users' }]}
              selectedIds={hasSelectedAllUser ? [0] : []}
              onSelectCheckbox={(_) => {
                const allRoles = [];
                if (!hasSelectedAllUser) {
                  userRoles.forEach((role) => {
                    allRoles.push(role.id);
                  });
                }
                setSelectedUserRoles(allRoles);
              }}
            />
            <CheckList
              classNames="cb-vertical"
              titleName={'label'}
              items={userRoles}
              selectedIds={selectedUserRoles}
              onSelectCheckbox={(item) => {
                const idx = selectedUserRoles.indexOf(item.id);
                const newSelectedUserRoles =
                  idx === -1
                    ? [...selectedUserRoles, item.id]
                    : [...selectedUserRoles.slice(0, idx), ...selectedUserRoles.slice(idx + 1)];
                setSelectedUserRoles(newSelectedUserRoles);
              }}
            />
          </div>
        )}
        renderCustomErrors={() => (
          <React.Fragment>
            {failedFields.includes('userRoles') && (
              <span id="userRoleError" role="alert" className="input-error">
                Please select role.
              </span>
            )}
          </React.Fragment>
        )}
      />

      <FormButtons
        isBusy={isSaving}
        submitLabel={isEdit ? 'Save' : 'Create'}
        onSubmit={onSubmit}
        onCancel={'/messageUsers'}
        wrapperClassNames={'mTop-40'}
        extraButtonProps={{
          id: isEdit ? 'button-banner-save' : 'button-banner-create',
        }}
      />
    </form>
  );
};

export default AddBanner;
